import React from "react";
import { Modal } from "react-bootstrap";
import {
  CreditCardIcon,
  ArrowDownIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";

const FinancialDashboardModal = ({ show, onHide, financialData }) => {
  const { depositByBank, companyTotalSales } = financialData || {};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "ETB",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getBankIcon = (bankId) => {
    const iconMap = {
      "short-term-credit": "💳",
      credit: "💰",
      wallet: "👛",
    };
    return iconMap[bankId] || "🏛️";
  };

  const getTotalDeposits = () => {
    return (
      depositByBank?.deposit?.reduce((sum, item) => sum + item.amount, 0) || 0
    );
  };

  const getTotalShortTermCredit = () => {
    return (
      depositByBank?.shortTermCredit?.reduce(
        (sum, item) => sum + item.amount,
        0
      ) || 0
    );
  };

  const getTotalSales = () => {
    return (
      companyTotalSales?.bookingsByPayment?.reduce(
        (sum, item) => sum + item.total,
        0
      ) || 0
    );
  };

  const getTotalRefunds = () => {
    return (
      companyTotalSales?.refund?.reduce(
        (sum, item) => sum + item.refundedAmount,
        0
      ) || 0
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="financial-dashboard-modal"
      className="financial-dashboard"
    >
      <Modal.Header closeButton className="text-black border-0">
        <Modal.Title className="flex gap-3 items-center text-2xl font-bold">
          <ChartBarIcon className="w-8 h-8" />
          Financial Dashboard
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-6 bg-gray-50">
        {/* Summary Cards */}
        {/* <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
          <div className="p-6 text-white bg-gradient-to-br from-green-400 to-green-600 rounded-xl shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-green-100">
                  Total Deposits
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(getTotalDeposits())}
                </p>
              </div>
              <ArrowUpIcon className="w-8 h-8 text-green-200" />
            </div>
          </div>

          <div className="p-6 text-white bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-orange-100">
                  Short Term Credit
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(getTotalShortTermCredit())}
                </p>
              </div>
              <CreditCardIcon className="w-8 h-8 text-orange-200" />
            </div>
          </div>

          <div className="p-6 text-white bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-blue-100">Total Sales</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(getTotalSales())}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 text-white bg-gradient-to-br from-red-400 to-red-600 rounded-xl shadow-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-red-100">
                  Total Refunds
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(getTotalRefunds())}
                </p>
              </div>
              <ArrowDownIcon className="w-8 h-8 text-red-200" />
            </div>
          </div>
        </div> */}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Bank Deposits Section */}
          <div className="p-6 bg-white rounded-xl shadow-lg">
            <div className="flex gap-3 items-center mb-6">
              {/* <BuildingLibraryIcon className="w-6 h-6 text-blue-600" /> */}
              <h3 className="text-xl font-bold text-gray-800">Bank Deposits</h3>
            </div>

            <div className="space-y-4">
              {depositByBank?.deposit?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <div className="flex gap-3 items-center">
                    <span className="text-2xl">{getBankIcon(item._id)}</span>
                    <div>
                      <p className="font-semibold text-gray-800 capitalize">
                        {item._id.replace("-", " ")}
                      </p>
                      <p className="text-sm text-gray-500">Bank Deposit</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(item.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {depositByBank?.shortTermCredit?.length > 0 && (
              <>
                <div className="flex gap-3 items-center mt-8 mb-4">
                  <CreditCardIcon className="w-5 h-5 text-orange-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    Short Term Credits
                  </h4>
                </div>

                <div className="space-y-3">
                  {depositByBank.shortTermCredit.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-4 bg-orange-50 rounded-lg transition-colors hover:bg-orange-100"
                    >
                      <div className="flex gap-3 items-center">
                        <span className="text-2xl">
                          {getBankIcon(item._id)}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-800 capitalize">
                            {item._id}
                          </p>
                          <p className="text-sm text-orange-600">
                            Short Term Credit
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-orange-600">
                          {formatCurrency(item.amount)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Company Sales Section */}
          <div className="p-6 bg-white rounded-xl shadow-lg">
            <div className="flex gap-3 items-center mb-6">
              {/* <Wallet className="w-6 h-6 text-purple-600" /> */}
              <h3 className="text-xl font-bold text-gray-800">
                Sales & Payments
              </h3>
            </div>

            <div className="space-y-4">
              {companyTotalSales?.bookingsByPayment?.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-gray-100"
                >
                  <div className="flex gap-3 items-center">
                    <span className="text-2xl">{getBankIcon(item._id)}</span>
                    <div>
                      <p className="font-semibold text-gray-800 capitalize">
                        {item._id.replace("-", " ")}
                      </p>
                      <p className="text-sm text-gray-500">Payment Method</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(item.total)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {companyTotalSales?.refund?.length > 0 && (
              <>
                <div className="flex gap-3 items-center mt-8 mb-4">
                  <ArrowDownIcon className="w-5 h-5 text-red-600" />
                  <h4 className="text-lg font-semibold text-gray-800">
                    Refunds
                  </h4>
                </div>

                <div className="space-y-3">
                  {companyTotalSales.refund.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-4 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
                    >
                      <div className="flex gap-3 items-center">
                        <span className="text-2xl">
                          {getBankIcon(item._id)}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-800 capitalize">
                            {item._id}
                          </p>
                          <p className="text-sm text-red-600">
                            Refunded Amount
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">
                          -{formatCurrency(item.refundedAmount)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Net Summary */}
        <div className="p-6 mt-8 text-white bg-gradient-to-r from-gray-600 to-gray-700 rounded-xl">
          <h3 className="mb-4 text-xl font-bold text-center text-white">
            Financial Summary
          </h3>
          <div className="grid grid-cols-1 gap-6 text-center md:grid-cols-3">
            <div>
              <p className="text-sm text-gray-300">Total Deposits</p>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(getTotalDeposits() + getTotalShortTermCredit())}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-300">Short Term Credit</p>
              <p className="text-2xl font-bold text-yellow-400">
                {formatCurrency(getTotalShortTermCredit())}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-300">Total Sales</p>
              <p className="text-2xl font-bold text-blue-400">
                {formatCurrency(getTotalSales())}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="bg-white border-t border-gray-200">
        <button
          onClick={onHide}
          className="px-6 py-2 font-medium text-white bg-gray-600 rounded-lg transition-colors hover:bg-gray-700"
        >
          Close Dashboard
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default FinancialDashboardModal;
